import { Injectable }       from '@angular/core';
import { NgbModal }         from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';

import { ApiService }                   from '../api/api.service';
import { CodeTypeModel }                from '../core/models/code-type';
import { CompanyService }               from '../companies/company.service';
import { ConfirmService }               from '../messages/confirm/confirm.service';
import { PriceMatrixItemModel }         from './typings/price-matrix.item';
import { PriceMatrixModel }             from './typings/price-matrix.matrix';
import { PriceMatrixAddComponent }      from './price-matrix.add.component';
import { PriceMatrixAddGroupComponent } from './price-matrix.add-group.component';
import { PriceMatrixUpdateModel }       from './typings/price-matrix.update-matrix';

@Injectable({
  providedIn: 'root'
})
export class PriceMatrixService {
  // Cached customer Price Type list embeds every matrix, so matrix writes must drop it
  _CUSTOMER_PRICE_TYPES_CACHE = 'customerPriceTypes';

  constructor (
    private apiService      : ApiService,
    private companyService  : CompanyService,
    private confirmService  : ConfirmService,
    private modalService    : NgbModal,
    private translateService: TranslateService
  ) {}

  add () : Promise<PriceMatrixModel> {
    return this.getTypes()
      .then(( types : Array<CodeTypeModel>) => {
        const modalRef = this.modalService.open(PriceMatrixAddComponent);

        modalRef.componentInstance.types = types;

        return modalRef.result;
      });
  }

  addAllProductGroups ( matrix : PriceMatrixModel ) : Promise<Array<PriceMatrixItemModel>> {
    return this.confirmService.generic(this.translateService.instant('PRICE_MATRIX_SPACE.ADD_ALL_PRODUCTS_WARNING'))
      .then(() => this.apiService.get('/price_matrices/add_all_product_groups_to_price_matrix', matrix.id));
  }

  addGroup ( matrix : PriceMatrixModel ) : Promise<Array<PriceMatrixItemModel>> {
    return this.getExcludedGroups(matrix)
      .then(( groups : Array<PriceMatrixItemModel>) => {
        const modalRef = this.modalService.open(PriceMatrixAddGroupComponent);

        modalRef.componentInstance.groups = groups;

        return modalRef.result;
      })
      .then(( groups : Array<PriceMatrixItemModel> ) => this.apiService.post('/price_matrix_items', {
          matrix: matrix,
          groups: groups
        }, 'price_matrix_item')
      );
  }

  addMarkupRange ( matrix : PriceMatrixModel ) : Promise<Array<PriceMatrixItemModel>> {
    return this.apiService.post('/price_matrix_items', {
      matrix: matrix,
      groups: [ { name: '' } ]
    }, 'price_matrix_item');
  }

  canPushToOtherBranches () : boolean {
    return this.companyService.isSubscriptionPlan('platinum')
      && this.companyService.isBranchOwner();
  }

  create ( matrix : PriceMatrixModel ) : Promise<PriceMatrixModel> {
    return this.apiService.post('/price_matrices', matrix, 'price_matrix', null, {
      invalidates: this._CUSTOMER_PRICE_TYPES_CACHE
    });
  }

  delete ( matrix : PriceMatrixModel ) : Promise<any> {
    return this.confirmService.generic(this.translateService.instant('PRICE_MATRIX_SPACE.DELETE_MATRIX', {
        code: matrix.matrix_name
      }))
      .then(() => this.apiService.delete('/price_matrix', matrix.id, {
        invalidates: this._CUSTOMER_PRICE_TYPES_CACHE
      }));
  }

  deleteItem ( item : PriceMatrixItemModel ) : Promise<Array<PriceMatrixItemModel>> {
    return this.confirmService.generic(this.translateService.instant('PRICE_MATRIX_SPACE.DELETE_ITEM', {
        code: item.product_group_name
      }))
      .then(() => this.apiService.delete('/price_matrix_item', item.id));
  }

  get () : Promise<Array<PriceMatrixModel>> {
    return this.apiService.get('/price_matrices');
  }

  getExcludedGroups ( matrix : PriceMatrixModel ) : Promise<Array<PriceMatrixItemModel>> {
    return this.apiService.get('/price_matrices/groups_not_in_matrix', matrix.id);
  }

  getItems ( matrix : PriceMatrixModel ) : Promise<Array<PriceMatrixItemModel>> {
    return this.apiService.get('/price_matrix_items', matrix.id)
      .then(( items : Array<PriceMatrixItemModel> ) => items.map(item => {
          item.destroy = '0';

          return item;
        })
      );
  }

  getTypes () : Promise<Array<CodeTypeModel>> {
    return this.apiService.get('/system/price_matrix_types/', null, 'price_matrix_types');
  }

  getUpdateTypes () : Promise<Array<CodeTypeModel>> {
    return this.apiService.get('/system/price_matrix_update_types/', null, 'price_matrix_update_types');
  }

  pushToOtherBranches ( matrix : PriceMatrixModel ) : Promise<string> {
    return Promise.resolve(() => this.canPushToOtherBranches()
        ? Promise.resolve()
        : Promise.reject())
      .then(() => this.confirmService.generic(this.translateService.instant('PRICE_MATRIX_SPACE.MATRIX_DOES_NOT_EXIST_IN_OTHER_BRANCHES')))
      .then(() => this.apiService.get('/price_matrix/push_to_branches', matrix.id))
      .then(( existed : boolean ) => existed
        ? this.translateService.instant('PRICE_MATRIX_SPACE.MATRIX_UPDATED_ON_BRANCHES')
        : this.translateService.instant('PRICE_MATRIX_SPACE.MATRIX_ADDED_TO_BRANCHES'));
  }

  update ( matrix : PriceMatrixModel, items : Array<PriceMatrixItemModel> ) : Promise<{ matrix : PriceMatrixModel; matrix_item : Array<PriceMatrixItemModel> }> {
    return this.apiService.patch('/price_matrix', {
        matrix_id   : matrix.id,
        matrix_items: items,
        price_matrix: matrix
      } , 'price_matrix_item', null, {
        invalidates: this._CUSTOMER_PRICE_TYPES_CACHE
      });
  }

  updatePercentages ( matrix : PriceMatrixModel, updateMatrix : PriceMatrixUpdateModel ) : Promise<any> {
    return this.confirmService.generic(this.translateService.instant(updateMatrix.updateType === 'zero_only'
        ? 'PRICE_MATRIX_SPACE.UPDATE_PRICE_MATRIX_WARNING_ZERO'
        : 'PRICE_MATRIX_SPACE.UPDATE_PRICE_MATRIX_WARNING_ALL'))
      .then(() => this.apiService.post('/price_matrix_item/update_percentages', {
          matrix_id  : matrix.id,
          percentage : updateMatrix.percentage,
          update_type: updateMatrix.updateType
        }, 'update_percentages'));
  }
}
